/**
 * File: TranslationDashboard.jsx
 * Path: /src/components/solutions/TranslationDashboard.jsx
 * Description: Standalone dashboard visualization for the Translation AI solution
 * Key Features: Interactive translation quality metrics and language analytics
 */

import React from 'react';
import { motion } from 'framer-motion';

const TranslationDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Translation Analytics Dashboard</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Main Dashboard Content */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Translation Accuracy */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Translation Accuracy</div>
                <div className="text-3xl font-bold mb-2">97.2%</div>
                <div className="text-xs text-indigo-200">+0.8% from last month</div>
              </div>
              
              {/* Languages Supported */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Languages Supported</div>
                <div className="text-3xl font-bold mb-2">53</div>
                <div className="text-xs text-green-300">+3 from last quarter</div>
              </div>
              
              {/* Average Translation Time */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-sm font-medium mb-2">Avg. Translation Time</div>
                <div className="text-3xl font-bold mb-2">2.3s</div>
                <div className="text-xs text-green-300">-0.5s from last month</div>
              </div>
            </div>
            
            {/* Top Language Pairs */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium">Top Language Pairs</div>
                <div className="text-xs text-white/60">Last 30 days</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  {/* Language Pair 1 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>English → Spanish</div>
                      <div>32%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '32%' }}></div>
                    </div>
                  </div>
                  
                  {/* Language Pair 2 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>English → French</div>
                      <div>24%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '24%' }}></div>
                    </div>
                  </div>
                  
                  {/* Language Pair 3 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>English → German</div>
                      <div>18%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '18%' }}></div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Language Pair 4 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>English → Chinese</div>
                      <div>9%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '9%' }}></div>
                    </div>
                  </div>
                  
                  {/* Language Pair 5 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>English → Japanese</div>
                      <div>7%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '7%' }}></div>
                    </div>
                  </div>
                  
                  {/* Language Pair 6 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Other Pairs</div>
                      <div>10%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '10%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Translation Quality and Top Challenges */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Translation Quality by Document Type */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4">
                <div className="text-sm font-medium mb-4">Quality by Document Type</div>
                
                <div className="space-y-3">
                  {/* Document Type 1 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Contracts</div>
                      <div className="text-green-300">98.4%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: '98.4%' }}></div>
                    </div>
                  </div>
                  
                  {/* Document Type 2 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Court Filings</div>
                      <div className="text-green-300">97.8%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: '97.8%' }}></div>
                    </div>
                  </div>
                  
                  {/* Document Type 3 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Patents</div>
                      <div className="text-green-300">96.9%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: '96.9%' }}></div>
                    </div>
                  </div>
                  
                  {/* Document Type 4 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Correspondence</div>
                      <div className="text-green-300">96.2%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: '96.2%' }}></div>
                    </div>
                  </div>
                  
                  {/* Document Type 5 */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <div>Academic Legal Papers</div>
                      <div className="text-green-300">94.8%</div>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: '94.8%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Top Translation Challenges */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4">
                <div className="text-sm font-medium mb-4">Top Translation Challenges</div>
                
                <div className="space-y-3">
                  {/* Challenge item 1 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Jurisdiction-specific legal terms</div>
                        <div className="text-xs text-white/60">5.2% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-500 rounded-full" style={{ width: '5.2%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Challenge item 2 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Complex legal citations</div>
                        <div className="text-xs text-white/60">3.8% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-500 rounded-full" style={{ width: '3.8%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Challenge item 3 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Latin legal phrases</div>
                        <div className="text-xs text-white/60">2.4% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '2.4%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Challenge item 4 */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Industry-specific jargon</div>
                        <div className="text-xs text-white/60">1.9% error rate</div>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '1.9%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* World Map */}
            <div className="mt-6 bg-white/5 backdrop-blur-sm rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium">Global Translation Coverage</div>
                <div className="text-xs text-white/60">53 Languages</div>
              </div>
              
              {/* Simplified world map visualization */}
              <div className="h-40 relative">
                {/* The simplified world map representation */}
                <svg width="100%" height="100%" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
                  {/* World map outline in light color */}
                  <path 
                    d="M122,108C122,108,131,100,136,100C141,100,155,108,155,108C155,108,169,117,175,117C181,117,198,108,198,108C198,108,205,100,211,100C217,100,222,108,222,108C222,108,226,117,232,117C238,117,252,108,252,108C252,108,257,100,263,100C269,100,286,117,286,117C286,117,294,125,300,125C306,125,314,117,314,117C314,117,320,108,326,108C332,108,345,117,345,117C345,117,359,125,365,125C371,125,379,117,379,117C379,117,383,108,389,108C395,108,406,117,406,117C406,117,414,125,420,125C426,125,431,117,431,117C431,117,442,108,448,108C454,108,462,117,462,117C462,117,471,125,477,125C483,125,496,117,496,117C496,117,505,108,511,108C517,108,529,117,529,117C529,117,537,125,543,125C549,125,566,108,566,108C566,108,574,100,580,100C586,100,595,108,595,108C595,108,603,117,609,117C615,117,623,108,623,108C623,108,632,100,638,100C644,100,650,108,650,108C650,108,659,117,665,117C671,117,676,108,676,108C676,108,684,100,690,100C696,100,700,108,700,108C700,108,709,117,715,117C721,117,729,108,729,108C729,108,749,100,749,100"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                  />
                  
                  {/* North America */}
                  <circle cx="200" cy="150" r="15" fill="rgba(79,70,229,0.4)" />
                  <text x="200" y="150" textAnchor="middle" fill="white" fontSize="8" dy=".3em">NA</text>
                  
                  {/* South America */}
                  <circle cx="250" cy="250" r="12" fill="rgba(79,70,229,0.4)" />
                  <text x="250" y="250" textAnchor="middle" fill="white" fontSize="8" dy=".3em">SA</text>
                  
                  {/* Europe */}
                  <circle cx="400" cy="150" r="18" fill="rgba(79,70,229,0.8)" />
                  <text x="400" y="150" textAnchor="middle" fill="white" fontSize="8" dy=".3em">EU</text>
                  
                  {/* Asia */}
                  <circle cx="550" cy="180" r="20" fill="rgba(79,70,229,0.6)" />
                  <text x="550" y="180" textAnchor="middle" fill="white" fontSize="8" dy=".3em">AS</text>
                  
                  {/* Africa */}
                  <circle cx="430" cy="230" r="14" fill="rgba(79,70,229,0.5)" />
                  <text x="430" y="230" textAnchor="middle" fill="white" fontSize="8" dy=".3em">AF</text>
                  
                  {/* Australia */}
                  <circle cx="650" cy="280" r="10" fill="rgba(79,70,229,0.4)" />
                  <text x="650" y="280" textAnchor="middle" fill="white" fontSize="8" dy=".3em">AU</text>
                  
                  {/* Connection lines */}
                  <path d="M200,150 L400,150" stroke="rgba(79,70,229,0.4)" strokeWidth="1" strokeDasharray="4,2" />
                  <path d="M400,150 L550,180" stroke="rgba(79,70,229,0.6)" strokeWidth="1" strokeDasharray="4,2" />
                  <path d="M400,150 L430,230" stroke="rgba(79,70,229,0.3)" strokeWidth="1" strokeDasharray="4,2" />
                  <path d="M550,180 L650,280" stroke="rgba(79,70,229,0.3)" strokeWidth="1" strokeDasharray="4,2" />
                  <path d="M250,250 L400,150" stroke="rgba(79,70,229,0.3)" strokeWidth="1" strokeDasharray="4,2" />
                </svg>
                
                {/* Legend */}
                <div className="absolute bottom-0 right-0 flex items-center text-xs text-white/70">
                  <div className="w-3 h-3 bg-indigo-500/80 rounded-full mr-1"></div>
                  <span>Translation Volume</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationDashboard;